/**
 * Bind TG MainButton to a screen. The MainButton is global per WebApp —
 * remember to clear it on unmount or it'll persist across routes.
 */

import { useEffect, useRef } from 'react';
import { useTg } from './useTelegram';

export interface MainButtonConfig {
  text: string;
  onClick: () => void;
  visible?: boolean;
  disabled?: boolean;
  loading?: boolean;
  color?: `#${string}`;
  textColor?: `#${string}`;
}

export function useMainButton(config: MainButtonConfig | null): void {
  const tg = useTg();
  // Keep latest onClick in ref so we can register a stable wrapper without re-binding on each render
  const onClickRef = useRef<(() => void) | null>(null);
  onClickRef.current = config?.onClick ?? null;

  useEffect(() => {
    if (!tg) return;
    const mb = tg.MainButton;
    if (!config) {
      mb.hide();
      return;
    }
    const handler = () => {
      onClickRef.current?.();
    };
    mb.setText(config.text);
    if (config.color) mb.color = config.color;
    if (config.textColor) mb.textColor = config.textColor;
    if (config.disabled) mb.disable();
    else mb.enable();
    if (config.loading) mb.showProgress();
    else mb.hideProgress();
    mb.onClick(handler);
    if (config.visible !== false) mb.show();
    else mb.hide();
    return () => {
      mb.offClick(handler);
      mb.hide();
    };
    // Use stable primitives only — onClick is delivered via ref above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tg, config?.text, config?.visible, config?.disabled, config?.loading, config?.color, config?.textColor]);
}
